import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';

import { DeviceDetectorService } from 'ngx-device-detector';
import { of, tap, catchError, concatMap } from 'rxjs';
import { CardElement, ElementsInstance, ApplePayInstance, ApplePayConfig } from '@recurly/recurly-js';
import {
  ApplePayPaymentAuthorizedEvent,
  ApplePayPaymentContact,
  ApplePayPaymentMethod,
} from '@recurly/recurly-js/lib/apple-pay/native';
import { TokenPayload } from '@recurly/recurly-js/lib/token';

import { AuthUser } from '@shared/signup/models';
import { RecurlyBillingInfo } from '@shared/recurly/models';
import {
  GlobalErrorHandler,
  ValidationService,
  emailValidator,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AccountApiService } from '@tablesready/frontend-store';

interface ApplePayPaymentDataHeader {
  applicationData: string;
  ephemeralPublicKey: string;
  publicKeyHash: string;
  transactionId: string;
}

interface ApplePayPaymentData {
  data: string;
  header: ApplePayPaymentDataHeader;
  signature: string;
  version: string;
}

interface ApplePayPaymentToken {
  paymentMethod: ApplePayPaymentMethod;
  paymentData: ApplePayPaymentData;
  transactionIdentifier: string;
}

interface ApplePayPaymentCustom {
  billingContact: ApplePayPaymentContact;
  token: ApplePayPaymentToken;
  recurlyToken: TokenPayload;
}

@Component({
  selector: 'tablesready-update-billing',
  templateUrl: './update-billing.component.html',
  styleUrls: ['./update-billing.component.scss'],
  providers: [GlobalErrorHandler],
})
export class UpdateBillingComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('cardRef') cardRef: ElementRef;
  @ViewChild('formRef', { read: ElementRef }) formRef: ElementRef;
  @Input() user: AuthUser;
  @Input() recurlyApiKey: string;
  @Input() title = 'Updating Payment Info';
  @Input() billingInfo?: RecurlyBillingInfo;
  @Input() billingEmail?: string;
  @Input() firstTimeAdding = false;

  icon = AppIcon;
  billingForm: UntypedFormGroup;
  elements: ElementsInstance;
  cardElement: CardElement;
  cardDataValid = false;
  browser: string = '';

  private applePay?: ApplePayInstance;

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly accountApiService: AccountApiService,
    private readonly errorHandler: GlobalErrorHandler,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly validationService: ValidationService,
    private readonly deviceDetectorService: DeviceDetectorService,
  ) {
    this.browser = this.deviceDetectorService.browser;
  }

  ngOnInit(): void {
    window.recurly.configure(this.recurlyApiKey);

    this.billingForm = this.formBuilder.group({
      email: [
        this.billingEmail,
        {
          validators: [],
          asyncValidators: emailValidator(this.validationService),
          updateOn: 'blur',
        },
      ],
      firstName: [this.billingInfo?.first_name],
      lastName: [this.billingInfo?.last_name],
      address: [this.billingInfo?.address1],
      postalCode: [this.billingInfo?.zip],
    });
  }

  ngAfterViewInit(): void {
    this.elements = window.recurly.Elements();
    this.attachAndStyleElements();

    if (this.isSafari) {
      window.recurly.ready(() => {
        const config: ApplePayConfig = {
          country: 'US',
          currency: 'USD',
          label: `Set billing info via Apple Pay for ${this.user?.account?.name} account`,
          total: '0',
          recurring: true,
          callbacks: {
            onPaymentAuthorized: (data): void => this.onPaymentAuthorized(data),
          },
        };
        this.applePay = window.recurly.ApplePay(config);

        this.applePay.ready(() => {
          const applePayButton = document.getElementById('applePayBtn');
          applePayButton.addEventListener('click', this.applePayBegin, false);
        });

        this.applePay.on('error', err => this.alertsService.addError(`Apple Pay error: ${err.message}`));
      });
    }
  }

  attachAndStyleElements(): void {
    this.cardElement = this.elements.CardElement({
      inputType: 'mobileSelect',
      style: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontColor: '#495057',
        fontWeight: '300',
        lineHeight: '1.5rem',
        placeholder: {
          content: {
            number: 'Card Number',
            expiry: 'MM/YY',
            cvv: 'CVC',
          },
        },
        invalid: { fontColor: 'red' },
      },
    });
    this.cardElement.attach(this.cardRef.nativeElement).on('change', (changeEvent) => {
      this.cardDataValid = changeEvent.valid;
    });
  }

  submit(): void {
    this.validationService.validateForm(this.billingForm);
    if (this.billingForm.invalid) {
      return;
    }

    this.billingForm.disable();
    window.recurly.token(this.elements, this.formRef.nativeElement, (err, token) => {
      if (err) {
        this.alertsService.add({
          duration: 5000,
          type: IAlertTypes.danger,
          local: true,
          heading: 'Error:',
          body: err.message,
        });
      } else {
        this.accountApiService.updateBillingInfo({recurlyToken: token.id })
          .pipe(
            concatMap(() => {
              const newEmail = { email: String(this.billingForm.get('email')?.value) };

              return this.accountApiService.updateRecurlyAccountInfo(newEmail);
            }),
            tap(() => {
              const message = this.firstTimeAdding ? 'payment information added.'
                : 'payment information updated.';

              this.alertsService.addSuccess(message);
              void this.modalController.dismiss(true);
            }),
            catchError((error) => {
              this.errorHandler.handleError(error);
              void this.modalController.dismiss(false);

              return of(null);
            }),
          )
          .subscribe();
      }
    });
  }

  get isSafari(): boolean {
    return this.browser === 'Safari';
  }

  cancel(): void {
    void this.modalController.dismiss(false);
  }

  ngOnDestroy(): void {
    this.cardElement.off('change');

    if (this.applePay) {
      this.applePay.off('error');

      document.getElementById('applePayBtn').removeEventListener('click', this.applePayBegin, false);
    }
  }

  private onPaymentAuthorized(data: ApplePayPaymentAuthorizedEvent): void {
    const { payment } = data;
    const { recurlyToken } = payment as unknown as ApplePayPaymentCustom;

    this.accountApiService.updateBillingInfo({ recurlyToken: recurlyToken.id })
      .pipe(
        tap(() => {
          this.alertsService.addSuccess('Billing info successfully updated');
          void this.modalController.dismiss();
        }),
        catchError(err => {
          this.alertsService.addError(`an error occurred during updating billing info: ${err.message}`);

          return of(null);
        }),
      )
      .subscribe();
  }

  private readonly applePayBegin = (): void => {
    this.applePay.begin();
  }
}
